import { usePostHog } from 'posthog-js/react';
import { useEffect, useRef } from 'react';

import { CluDemoVideo } from '@/components/appsumo/clu-demo-video.component';
import { AppIcon } from '@/components/ui/app-icon';
import {
  APPSUMO_GAIN_VISUALS,
  type PlanTile,
} from '@/components/upgrade/plan-tiles';
import { UpgradeCard } from '@/components/upgrade/upgrade-card.component';
import {
  appSumoGainCopy,
  appSumoGateCopy,
  appSumoGatePriceLine,
  appSumoUpgradeSearch,
  BOOK_A_CALL,
  orderGains,
  planTag,
  safeCallUrl,
  SEE_YOUR_PRICE,
  trackAppSumoUpgrade,
  type AppSumoGateFeature,
  type AppSumoPlanPrices,
} from '@/lib/appsumo-upgrade';
import { iconSpinnerThird } from '@/lib/icons';
import type {
  AppSumoUpgradeOffer,
  AppSumoUpgradePhase,
} from '@/store/features/appsumo.feature';

/** A locked screen while the upgrade offer loads: it waits rather than show
 *  the plans card and swap it for the offer a moment later. */
export function AppSumoGateLoading() {
  return (
    <div className='flex min-h-0 w-full flex-1 items-center justify-center py-16'>
      <AppIcon
        icon={iconSpinnerThird}
        aria-hidden
        className='animate-spin text-muted-foreground'
        style={{ fontSize: 20 }}
      />
    </div>
  );
}

/** What the plans add for this tier, as tiles tagged with the plans that
 *  have each: the server's list in its order, with the locked screen's own
 *  feature first. */
function gainTiles(
  offer: AppSumoUpgradeOffer,
  feature: AppSumoGateFeature
): PlanTile[] {
  return orderGains(offer.gains, feature).gains.map((gain) => ({
    label: appSumoGainCopy(gain.key, offer).title,
    note: planTag(gain.plans),
    visual: APPSUMO_GAIN_VISUALS[gain.key],
  }));
}

/**
 * A screen an AppSumo-only workspace's deal doesn't include, while the
 * upgrade price is open: what the screen is, a short demo where there is one
 * (Clu), what each plan adds for this tier, the AppSumo prices beside the
 * regular ones, and two ways on: the plan wizard at the member price, on the
 * cheapest plan with the screen's feature (Pro for a generic screen), or a
 * call with Sameer. `title` is a generic
 * screen's own.
 */
export function AppSumoUpgradeGate({
  feature,
  offer,
  prices,
  phase,
  title,
  className,
}: {
  feature: AppSumoGateFeature;
  offer: AppSumoUpgradeOffer;
  prices: AppSumoPlanPrices;
  phase: AppSumoUpgradePhase | null;
  title?: string;
  className?: string;
}) {
  const posthog = usePostHog();
  const tier = offer.appsumo_tier;

  // Once per locked screen shown, not on every refetch of the offer.
  const viewed = useRef(false);
  useEffect(() => {
    if (viewed.current) return;
    viewed.current = true;
    trackAppSumoUpgrade(posthog, 'appsumo_upgrade_gate_viewed', {
      feature,
      tier,
      phase,
    });
  }, [feature, tier, phase, posthog]);

  const copy = appSumoGateCopy(feature, offer, prices, title);
  const callUrl = safeCallUrl(offer.call_url);

  return (
    <UpgradeCard
      title={copy.title}
      subtitle={copy.line}
      media={feature === 'clu' ? <CluDemoVideo /> : undefined}
      tiles={feature === 'clu' ? [] : gainTiles(offer, feature)}
      action={{
        label: SEE_YOUR_PRICE,
        to: '/upgrade',
        search: appSumoUpgradeSearch({
          source: 'gate',
          plan: feature === 'generic' ? 'pro' : 'starter',
        }),
        onClick: () =>
          trackAppSumoUpgrade(posthog, 'appsumo_upgrade_gate_clicked', {
            feature,
            tier,
          }),
      }}
      secondary={
        callUrl
          ? {
              label: BOOK_A_CALL,
              href: callUrl,
              onClick: () =>
                trackAppSumoUpgrade(posthog, 'appsumo_upgrade_call_clicked', {
                  source: 'gate',
                  tier,
                }),
            }
          : undefined
      }
      legal={appSumoGatePriceLine(prices, offer.ends_at)}
      className={className}
    />
  );
}
